console.info("[MadSportz.boot] STEP 1: main.tsx module loaded");
window.__mszBootPhase = "main:module";

function recoveryTakingTooLongHtml(): string {
  return (
    '<div style="min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:1.5rem;font-family:system-ui,sans-serif;background:rgb(255, 247, 237);color:rgb(154, 52, 18);text-align:center;max-width:26rem;margin:0 auto">' +
    '<p style="font-weight:600;margin:0 0 0.5rem">MadSportz is taking too long</p>' +
    '<p style="margin:0;font-size:0.875rem;line-height:1.5">Try a hard refresh. If this started after an update, open the site once with <code style="word-break:break-all">?msz_clear_sw=1</code> in the URL to clear the service worker and caches, then reload.</p>' +
    "</div>"
  );
}

async function clearSwAndCaches(): Promise<void> {
  if ("caches" in window && typeof caches.keys === "function") {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  }
  if ("serviceWorker" in navigator) {
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms / 1000}s (slow network or blocked request).`));
    }, ms);
    promise.then(
      (v) => {
        window.clearTimeout(id);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(id);
        reject(e);
      },
    );
  });
}

function showMainCatchError(err: unknown): void {
  window.__mszBootFailed = true;
  window.__mszBootPhase = "main:error";
  console.error(err);
  const root = document.getElementById("root");
  if (!root) return;
  const detail =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : "The application scripts could not be loaded.";
  root.innerHTML =
    '<div style="min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;padding:1.5rem;font-family:system-ui,sans-serif;background:rgb(254, 242, 242);color:rgb(153, 27, 27);text-align:center;max-width:28rem;margin:0 auto">' +
    '<p style="font-weight:600;margin:0 0 0.5rem">MadSportz could not start</p>' +
    `<p style="margin:0;font-size:0.875rem;line-height:1.5;word-break:break-word">${detail}</p>` +
    '<p style="margin:1rem 0 0;font-size:0.8rem;color:rgb(100, 116, 139)">Try a hard refresh, add ?msz_clear_sw=1 once to clear workers and caches, or use another network.</p>' +
    "</div>";
}

async function boot(): Promise<void> {
  window.__mszBootPhase = "main:boot()";
  console.info("[MadSportz.boot] STEP 1b: boot() started");

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    params = new URLSearchParams();
  }

  if (params.has("msz_boot_debug")) {
    const { logImportMetaEnvDiagnostics } = await import("./boot-env-log");
    logImportMetaEnvDiagnostics();
  }

  if (params.has("msz_clear_sw")) {
    await clearSwAndCaches();
    const u = new URL(window.location.href);
    u.searchParams.delete("msz_clear_sw");
    window.location.replace(u.pathname + u.search + u.hash);
    return;
  }

  // Do not gate boot on import.meta.env here: Vite inlines env at build time; if vars are
  // unset during `vite build`, Rollup may tree-shake the entire app as dead code. Missing
  // env is still caught when the Supabase client module loads (see integrations/supabase/client).

  window.__mszModule = true;
  window.__mszBootPhase = "main:import(bootstrap-launch)";
  console.info("[MadSportz.boot] STEP 3: calling bootstrap-launch (dynamic import)…");

  window.addEventListener(
    "unhandledrejection",
    (e) => {
      const err = e.reason
      if (err && typeof err.message === 'string' && (
        err.message.includes('Failed to fetch dynamically imported module') ||
        err.message.includes('ChunkLoadError') ||
        err.message.includes('Loading chunk')
      )) {
        e.preventDefault()
        console.warn('[MadSportz] Chunk load failure, clearing caches and reloading...', err.message)
        caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k)))).finally(() => {
          window.location.reload()
        })
        return
      }
      console.error("[MadSportz] Unhandled promise rejection during boot", e.reason);
    },
    { once: true },
  );

  const softTimer = window.setTimeout(() => {
    if (!window.__mszReady) {
      console.warn(
        "[MadSportz] Boot still in progress after 20s (slow network, large bundles, or blocked requests).",
      );
    }
  }, 20000);

  const hardTimer = window.setTimeout(() => {
    if (!window.__mszAppMounted) {
      console.error("[MadSportz] App did not mount within 60s");
      const root = document.getElementById("root");
      if (!root) return;
      // Only replace if root still has the boot-splash (no rendering occurred)
      const txt = (root.innerText ?? "").trim();
      if (txt.includes("Loading MadSportz")) {
        root.innerHTML = recoveryTakingTooLongHtml();
      }
    }
  }, 60000);

  const clearBootTimers = () => {
    window.clearTimeout(softTimer);
    window.clearTimeout(hardTimer);
  };

  window.addEventListener("msz:app-mounted", clearBootTimers, { once: true });

  try {
    await withTimeout(
      import("./bootstrap-launch").then((m) => m.launch()),
      240_000,
      "MadSportz startup",
    );
  } catch (err) {
    clearBootTimers();
    showMainCatchError(err);
  }
}

export function startBoot(): void {
  void boot();
}
